import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { FloatingField } from '@/components/ui/floating-field';
import { DateField } from '@/components/ui/date-field';
import { Combobox } from '@/components/ui/combobox';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatCurrency, formatDate } from '@/lib/utils';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiBase, getToken } from '@/lib/api';

function toYmd(d) {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export default function InvoicesPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [voidId, setVoidId] = useState('');
  const [form, setForm] = useState({
    id: `INV-${Date.now()}`,
    load: '',
    customer: '',
    due: new Date(Date.now() + 14 * 86400000),
    overridePod: false,
    lineItems: [{ description: 'Freight', amount: '' }],
  });

  const dataQ = useQuery({
    queryKey: ['invoices-page'],
    queryFn: async () => {
      const [inv, l, p] = await Promise.all([
        api('/api/v1/invoices'),
        api('/api/v1/loads'),
        api('/api/v1/shippers'),
      ]);
      if (inv.status !== 200) throw new Error(inv.data?.error ?? 'Failed to load invoices');
      if (l.status !== 200) throw new Error(l.data?.error ?? 'Failed to load loads');
      return {
        invoices: inv.data.invoices ?? [],
        loads: l.data.loads ?? [],
        parties: p.status === 200 ? p.data.shippers ?? [] : [],
      };
    },
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load invoices');
  }, [dataQ.isError, dataQ.error]);

  const invoices = useMemo(() => {
    const list = dataQ.data?.invoices ?? [];
    return [...list].sort((a, b) =>
      String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''))
    );
  }, [dataQ.data?.invoices]);
  const loads = dataQ.data?.loads ?? [];
  const parties = dataQ.data?.parties ?? [];

  useEffect(() => {
    if (!form.load) return;
    const load = loads.find((l) => l.id === form.load);
    if (!load) return;
    setForm((prev) => {
      const next = [...prev.lineItems];
      if (!next[0]) next[0] = { description: 'Freight', amount: '' };
      next[0] = {
        description: `Freight ${load.loadNumber}`,
        amount: ((load.rate || 0) / 100).toFixed(2),
      };
      return { ...prev, lineItems: next };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when load selection changes
  }, [form.load, loads]);

  const addLineItem = () => {
    setForm({ ...form, lineItems: [...form.lineItems, { description: '', amount: '' }] });
  };

  const removeLineItem = (idx) => {
    setForm({ ...form, lineItems: form.lineItems.filter((_, i) => i !== idx) });
  };

  const updateLineItem = (idx, field, value) => {
    const items = [...form.lineItems];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, lineItems: items });
  };

  const amountCents = useMemo(
    () =>
      form.lineItems.reduce(
        (sum, line) => sum + Math.round((parseFloat(line.amount) || 0) * 100),
        0
      ),
    [form.lineItems]
  );

  const handleCreate = async () => {
    const party = parties.find((p) => p.id === form.customer);
    if (!party) {
      toast.error('Select bill-to party');
      return;
    }
    if (amountCents <= 0) {
      toast.error('Add line amounts');
      return;
    }
    const dueDate = toYmd(form.due);
    const r = await api('/api/v1/invoices', {
      method: 'POST',
      body: {
        invoiceNumber: form.id.trim(),
        loadId: form.load || undefined,
        amountCents,
        billToName: party.name,
        billToId: party.id,
        dueDate: dueDate || undefined,
        skipDocGate: form.overridePod || undefined,
        lineItems: form.lineItems
          .filter((l) => l.description.trim())
          .map((l) => ({
            description: `${l.description.trim()}${dueDate ? ` (due ${dueDate})` : ''}`,
            amountCents: Math.round((parseFloat(l.amount) || 0) * 100),
          })),
      },
    });
    if (r.status === 201) {
      toast.success('Invoice created');
      setForm({
        id: `INV-${Date.now()}`,
        load: '',
        customer: '',
        due: new Date(Date.now() + 14 * 86400000),
        overridePod: false,
        lineItems: [{ description: 'Freight', amount: '' }],
      });
      setShowCreate(false);
      const created = r.data?.invoice;
      if (created) {
        qc.setQueryData(['invoices-page'], (prev) => {
          if (!prev) return prev;
          const list = Array.isArray(prev.invoices) ? prev.invoices : [];
          const without = list.filter((inv) => inv.id !== created.id);
          return { ...prev, invoices: [created, ...without] };
        });
      }
    } else if (r.status === 409 && r.data?.code === 'DOC_PACKET_INCOMPLETE') {
      toast.error(
        String(r.data.error ?? 'Missing POD') + (r.data.hint ? ` — ${r.data.hint}` : '')
      );
    } else {
      toast.error(String(r.data?.error ?? 'Create failed'));
    }
  };

  const sendInvoice = async (id) => {
    const r = await api(`/api/v1/invoices/${id}/send`, { method: 'POST', body: {} });
    if (r.status === 200) {
      toast.success('Invoice sent');
      qc.invalidateQueries({ queryKey: ['invoices-page'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const markPaid = async (id) => {
    const r = await api(`/api/v1/invoices/${id}/mark-paid`, { method: 'POST', body: {} });
    if (r.status === 200) {
      toast.success('Marked paid');
      qc.invalidateQueries({ queryKey: ['invoices-page'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const handleVoid = async () => {
    if (!voidId) return;
    const r = await api(`/api/v1/invoices/${voidId}/void`, { method: 'POST', body: {} });
    if (r.status === 200) {
      toast.success('Invoice voided');
      setVoidId('');
      qc.invalidateQueries({ queryKey: ['invoices-page'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const r = await api(`/api/v1/invoices/${deleteId}`, { method: 'DELETE' });
    if (r.status === 200) {
      toast.success('Invoice deleted');
      setDeleteId('');
      qc.invalidateQueries({ queryKey: ['invoices-page'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const openPdf = async (id) => {
    const token = getToken();
    const href = `${getApiBase()}/api/v1/invoices/${id}/pdf-layout`;
    try {
      const res = await fetch(href, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const text = await res.text();
        let message = `Invoice PDF failed (${res.status})`;
        try {
          const j = JSON.parse(text);
          if (j?.error) message = String(j.error);
        } catch {
          /* ignore */
        }
        toast.error(message);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) toast.error('Popup blocked — allow popups to view invoice');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.message ?? 'Invoice PDF failed');
    }
  };

  const columns = [
    {
      key: 'invoiceNumber',
      label: 'Invoice #',
      sortable: true,
      className: 'font-mono',
    },
    {
      key: 'loadId',
      label: 'Load',
      className: 'font-mono',
      render: (v) => (v ? String(v).slice(0, 8) : '—'),
    },
    { key: 'billToName', label: 'Customer', sortable: true },
    {
      key: 'amountCents',
      label: 'Amount',
      render: (v) => <span className="font-mono">{formatCurrency((v || 0) / 100)}</span>,
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (v) => (v ? formatDate(v) : '—'),
    },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex gap-1 flex-wrap justify-end">
          {row.status === 'draft' && (
            <Button size="sm" variant="outline" onClick={() => sendInvoice(row.id)}>
              Send
            </Button>
          )}
          {row.status !== 'paid' && row.status !== 'void' && row.status !== 'draft' && (
            <Button size="sm" variant="outline" onClick={() => markPaid(row.id)}>
              Mark Paid
            </Button>
          )}
          <Button size="sm" variant="ghost" title="PDF" onClick={() => openPdf(row.id)}>
            <Icons.FileText className="h-3 w-3" />
          </Button>
          {row.status !== 'paid' && row.status !== 'void' && (
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-destructive"
              title="Void invoice"
              onClick={() => setVoidId(row.id)}
            >
              <Icons.XCircle className="h-3 w-3" />
            </Button>
          )}
          {row.status === 'draft' && (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              title="Delete draft"
              onClick={() => setDeleteId(row.id)}
            >
              <Icons.Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Invoices"
          description="Create and manage customer invoices"
          icon="FileText"
          metrics={[
            { label: 'Total', value: invoices.length },
            {
              label: 'Unpaid',
              value: invoices.filter((i) => i.status !== 'paid' && i.status !== 'void').length,
            },
          ]}
          actions={
            <Button onClick={() => setShowCreate(!showCreate)}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          }
        />

        {showCreate && (
          <Card>
            <CardHeader>
              <CardTitle>New Invoice</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FloatingField
                  label="Invoice Number"
                  value={form.id}
                  onChange={(e) => setForm({ ...form, id: e.target.value })}
                  required
                />
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Load
                  </label>
                  <Combobox
                    options={loads.map((l) => ({
                      value: l.id,
                      label: `${l.loadNumber} ($${((l.rate || 0) / 100).toFixed(2)})`,
                    }))}
                    value={form.load}
                    onChange={(v) => setForm({ ...form, load: v })}
                    placeholder="Select load"
                    allowClear
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Bill To *
                  </label>
                  <Combobox
                    options={parties.map((p) => ({ value: p.id, label: p.name }))}
                    value={form.customer}
                    onChange={(v) => setForm({ ...form, customer: v })}
                    placeholder="Select customer"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Due Date
                  </label>
                  <DateField value={form.due} onChange={(d) => setForm({ ...form, due: d })} />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Line Items</h4>
                  <Button size="sm" variant="outline" onClick={addLineItem}>
                    <Icons.Plus className="h-3 w-3 mr-1" />
                    Add Line
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.lineItems.map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        value={item.description}
                        onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                        placeholder="Description"
                        className="flex-1 px-3 py-2 border rounded-md text-sm bg-background outline-none focus:ring-2 focus:ring-ring"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={item.amount}
                        onChange={(e) => updateLineItem(idx, 'amount', e.target.value)}
                        placeholder="Amount"
                        className="w-32 px-3 py-2 border rounded-md text-sm font-mono bg-background outline-none focus:ring-2 focus:ring-ring"
                      />
                      {form.lineItems.length > 1 && (
                        <Button size="icon" variant="ghost" onClick={() => removeLineItem(idx)}>
                          <Icons.X className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-3 pt-3 border-t">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-xl font-bold font-mono">
                      {formatCurrency(amountCents / 100)}
                    </p>
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.overridePod}
                  onCheckedChange={(c) => setForm({ ...form, overridePod: !!c })}
                />
                <span className="text-sm">Override missing POD requirement</span>
              </label>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate}>Create Invoice</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <DataTable
          columns={columns}
          data={invoices}
          emptyMessage={dataQ.isLoading ? 'Loading…' : 'No invoices yet'}
        />

        <ConfirmDialog
          open={Boolean(voidId)}
          onOpenChange={(open) => !open && setVoidId('')}
          title="Void invoice?"
          description="Mark as void? The invoice stays in history but can no longer be paid or sent."
          confirmLabel="Void"
          variant="destructive"
          onConfirm={handleVoid}
        />
        <ConfirmDialog
          open={Boolean(deleteId)}
          onOpenChange={(open) => !open && setDeleteId('')}
          title="Delete draft invoice?"
          description="Permanently delete this draft? Only draft invoices can be deleted."
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={handleDelete}
        />
      </div>
);
}
